import { ChangeDetectionStrategy, ChangeDetectorRef, Component } from '@angular/core';
import { FormGroup } from '@angular/forms';
import {
  addCardNumberInput,
  addIssuerReceiptEmailInput,
  enableCardNumberValidation,
  updateConfirmEmailValidator,
} from '@app/service-request-payment-input/presenter/service-request-payment-input-pres.form';
import { isStringEmpty, stringEmptyDefault } from '@common/helpers';
import {
  PAYMENT_REQUEST_PAYMENT_CARD_DEFAULT,
  PaymentMethodsType,
} from '@common/helpers/constants/service-request-payment-input/service-request-payment-input.constants';
import { PhoneNumberCountryType } from '@common/interfaces';
import { BrandImage } from '@common/interfaces/service-request-payment-input/service-request-payment-input.interface';
import { passwordInputPayloadParts } from '@common/services/service-request-payment-input/password-input';
import { ServiceRequestPaymentInputService } from '@common/services/service-request-payment-input/service-request-payment-input-service';
import { SupportComponent } from '@lib/components/support-class';
import { AswValidators } from '@lib/helpers';
import { DeviceType, LoginStatusType } from '@lib/interfaces';
import { StaticMsgPipe } from '@lib/pipes';
import { CommonLibService } from '@lib/services';
import { ModalService } from '@lib/services/modal/modal.service';
import { Subscription, combineLatest } from 'rxjs';
import { GetBasicReservationInformationResponseData } from 'src/sdk-amcmember';
import { GetOrderResponseData, PaymentRequestPaymentCard } from 'src/sdk-servicing';

@Component({
  selector: 'asw-service-request-payment-input-credit-card',
  templateUrl: './service-request-payment-input-credit-card.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ServiceRequestPaymentInputCreditCardComponent extends SupportComponent {
  _subscriptions: Subscription = new Subscription();

  public isSp: boolean = false;

  public isRealLogin: boolean = false;

  public paymentMethod!: string;

  public titleCreditCard: string = 'heading.creditCardOrDebitCardForTitle';

  public isDisplayCardSelecting: boolean = false;

  newCard: string = PAYMENT_REQUEST_PAYMENT_CARD_DEFAULT.NEW_CARD;

  paymentCard1: PaymentRequestPaymentCard.RegisteredCardTypeEnum =
    PaymentRequestPaymentCard.RegisteredCardTypeEnum.PaymentCard1;

  paymentCard2: PaymentRequestPaymentCard.RegisteredCardTypeEnum =
    PaymentRequestPaymentCard.RegisteredCardTypeEnum.PaymentCard2;

  public isDisplayCreditCardName1: boolean = false;

  public isDisplayCreditCardName2: boolean = false;

  public isDisplayUatpCheckbox: boolean = false;

  public isDisplayIssuerReceipt: boolean = false;

  public cardBrandImageList: Array<BrandImage> = [];

  public yearRange: number[] = [];

  public isShownRegistrationUpdateCheckbox: boolean = false;
  public amc: GetBasicReservationInformationResponseData = {};
  get isDisplayCardNumberAsTextBox(): boolean {
    return this._serviceRequestPaymentInputService.isDisplayCardNumberAsTextBox;
  }

  get cardNumberAsLabel(): boolean {
    return this._serviceRequestPaymentInputService.paymentInputFG.get('cardNumber')?.value;
  }

  get isSelectedCardSP(): boolean {
    return this._serviceRequestPaymentInputService.isSelectedCardSP;
  }

  get selectedCard(): string {
    return this._serviceRequestPaymentInputService.selectedCard;
  }

  get paymentInputFG(): FormGroup {
    return this._serviceRequestPaymentInputService.paymentInputFG ?? ({} as FormGroup);
  }

  get phoneNumberCountryList(): Array<PhoneNumberCountryType> {
    return this._serviceRequestPaymentInputService.phoneNumberCountryList;
  }

  get countryMap(): Map<string, string> {
    return this._serviceRequestPaymentInputService.countryMap;
  }

  constructor(
    public _common: CommonLibService,
    private readonly _changeDetectorRef: ChangeDetectorRef,
    private readonly _modalService: ModalService,
    public staticMsg: StaticMsgPipe,
    private readonly _serviceRequestPaymentInputService: ServiceRequestPaymentInputService
  ) {
    super(_common);
  }

  init(): void {
    this.isSp = this._common.aswContextStoreService?.aswContextData?.deviceType === DeviceType.SMART_PHONE;

    this.isRealLogin = this._common.aswContextStoreService?.aswContextData?.loginStatus === LoginStatusType.REAL_LOGIN;

    // change title for credit card
    if (this._common.aswContextStoreService?.aswContextData?.pointOfSaleId === 'TYONH08AP') {
      this.titleCreditCard = 'label.creditCard';
    }
    this._subscriptions.add(
      this._serviceRequestPaymentInputService.paymentMethod$.subscribe((paymentMethod) => {
        if (paymentMethod === PaymentMethodsType.CREDIT_CARD) {
          // add card number input for new card
          const cardNumber = addCardNumberInput();
          this._serviceRequestPaymentInputService.paymentInputFG.addControl('cardNumber', cardNumber);
          // [1.9] 領収書の発行名義
          this.isDisplayIssuerReceipt =
            this._common.aswContextStoreService.aswContextData.posCountryCode === 'JP' &&
            paymentMethod === PaymentMethodsType.CREDIT_CARD;
        }
        this._changeDetectorRef.markForCheck();
      })
    );
    const currentYmd = new Date();
    this.yearRange = [currentYmd.getFullYear(), currentYmd.getFullYear() + 14];

    this._serviceRequestPaymentInputService.onInitCreditCardSubject.subscribe(() => this.onInit());

    this._subscriptions.add(
      this._serviceRequestPaymentInputService.checkValid$.subscribe((checkValid) => {
        if (checkValid) {
          this._changeDetectorRef.detectChanges();
        }
      })
    );
  }

  onInit() {
    this._subscriptions.add(
      combineLatest([
        this._serviceRequestPaymentInputService.pnr$,
        this._serviceRequestPaymentInputService.amc$,
      ]).subscribe(([pnr, amc]) => {
        this.amc = amc;
        if (this.isDisplayIssuerReceipt) {
          this.initIssuerReceiptEmailInput(pnr);
        }

        // [6.1] 使用するクレジットカード選択
        this.isDisplayCardSelecting = this.initDisplayCardSelecting(amc);

        /**
         * [6.1.2] 選択中の項目はラベル表示とし、あわせて選択中を示すチェックマークを表示する。枠線はネイビーにする。
         * Check it out in html
         */

        /**
         * [6.1.3]初期表示の選択は新しいカードとする。
         * Init only one time
         */
        if (isStringEmpty(this.selectedCard)) {
          this._serviceRequestPaymentInputService.selectedCard = PAYMENT_REQUEST_PAYMENT_CARD_DEFAULT.NEW_CARD;
        }
        // [6.5] 初期は選択値がある場合でも非表示とする。
        // ※ユーザ共通情報.端末種別 = ""SP""のみ、使用するクレジットカード選択後に表示となる。"
        this._serviceRequestPaymentInputService.isSelectedCardSP = false;

        // [6.8] 主な利用可能であるカードブランド
        // [6.8.1]「当画面共通処理No5.利用可能カードブランドパターン取得処理」を実施する。
        const availableCardBrandPatterns = this._serviceRequestPaymentInputService.handleCommonNo3();
        // [6.8.2] 返却値の利用可能カードブランドパターンを”,”区切りで分割したリストを利用可能カードブランドリストとする。
        let availableCardBrandList = availableCardBrandPatterns.split(',');
        this.cardBrandImageList = [...this.onDisplayBrands(availableCardBrandList)];

        // [6.9] UATPカード選択チェック
        // [6.9.1] 利用可能カードブランドパターンに""TP""(UATP)を含む場合、未選択で表示する。
        // 未選択で表示する。
        if (availableCardBrandList.includes('TP')) {
          this.isDisplayUatpCheckbox = true;
          // this.paymentInputFG.get('uATPCardChecked')?.setValue(false);
        }
        // [6.9.2] 上記以外の場合、非表示とする。
        else {
          this.isDisplayUatpCheckbox = false;
        }

        // [6.10] クレジットカード番号
        this.onInitDataCreditCard();

        // [6.14] 予約基本情報クレジットカードの登録・更新チェック
        // 以下の条件全てを満たした場合、表示する。
        this.isShownRegistrationUpdateCheckbox = this.onIsShowSaveAsBasicReservationInformation(amc);

        // [6.15] 名義人メールアドレス
        if (!this.paymentInputFG.get('cardHolderEmail')?.value) {
          this.paymentInputFG
            .get('cardHolderEmail')
            ?.setValue(pnr?.contacts?.representative?.emails?.[0]?.address ?? '');
        }

        // [6.16] 名義人メールアドレス確認
        if (!this.paymentInputFG.get('cardHolderConfirmEmail')?.value) {
          this.paymentInputFG
            .get('cardHolderConfirmEmail')
            ?.setValue(pnr?.contacts?.representative?.emails?.[0]?.address ?? '');
        }

        // [6.17] 電話番号の国番号選択
        if (!this.paymentInputFG.get('cardHolderPhoneCountry')?.value) {
          // [6.17.1] PNR情報取得API応答.data.contacts.representative.phones[0].countryPhoneExtensionを取得し、SMS国とする。
          const countryPhoneExtension = pnr?.contacts?.representative?.phones?.[0]?.countryPhoneExtension;
          // [6.17.2] SMS国をキー、イベントID(1)で作成した電話番号対象国(マップ)から当該国コード(2 レター)を取得する。
          let countryCode = '';
          for (const [key, value] of this.countryMap) {
            if (value === countryPhoneExtension) {
              countryCode = key;
              break;
            }
          }
          // [6.17.3] キー=国コード(2 レター)である項目を表示する。
          this.paymentInputFG.get('cardHolderPhoneCountry')?.setValue(countryCode);
        }

        // [6.18] 電話番号の国番号
        this.getPhoneCountryExtension();

        // [6.19] 電話番号
        if (!this.paymentInputFG.get('cardHolderPhoneNumber')?.value) {
          this.paymentInputFG.get('cardHolderPhoneNumber')?.setValue('');
        }
        this.paymentInputFG?.get('cardHolderPhoneNumber')?.addValidators([
          AswValidators.phoneNumber({
            telCountryCode: this.countryMap.get(this.paymentInputFG.get('cardHolderPhoneCountry')?.value) ?? '',
            params: {
              key: 0,
              value: 'label.cardHolderTelNumber',
            },
          }),
        ]);

        // [6.19] 電話番号
        // [6.19.1] PNR情報取得API応答.data.contacts.representative.phones[0].number が存在する場合、PNR情報取得API応答.data.contacts.representative.phones[0].numberを表示する。
        if (pnr?.contacts?.representative?.phones?.[0]?.number) {
          this.paymentInputFG
            .get('cardHolderPhoneNumber')
            ?.setValue(pnr?.contacts?.representative?.phones?.[0]?.number);
        }
        // [6.19.2] PNR情報取得API応答.data.contacts.representative.phonesの要素数=0件の場合、””(空欄)とする。
        if (pnr?.contacts?.representative?.phones?.length === 0) {
          this.paymentInputFG.get('cardHolderPhoneNumber')?.setValue('');
        }
        this._changeDetectorRef.markForCheck();
      })
    );
  }

  get cardHolderCountryPhoneExtension() {
    return this._serviceRequestPaymentInputService.cardHolderCountryPhoneExtension;
  }

  onIsShowSaveAsBasicReservationInformation(amc: GetBasicReservationInformationResponseData) {
    // [6.14] 予約基本情報クレジットカードの登録・更新チェック
    /**
     * ユーザ共通情報.ログインステータス=”REAL_LOGIN"(ログイン済み)の場合、表示する。
     * ただし、使用するクレジットカード選択で新しいカードが選択されている かつ
     * 予約基本情報API応答.data.generalCreditCardName1≠””(空欄),かつ generalCreditCardName2≠””(空欄)の場合、非表示とする。
     */
    return (
      this.isRealLogin &&
      !(
        this.selectedCard === PAYMENT_REQUEST_PAYMENT_CARD_DEFAULT.NEW_CARD &&
        amc?.generalCreditCardName1 !== '' &&
        amc?.generalCreditCardName2 !== ''
      )
    );
  }

  onInitDataCreditCard() {
    let expiryDate = '';
    // [6.10.1] 使用するクレジットカード選択にて、クレジットカード1、またはクレジットカード2が選択されている場合、以下の値をラベルで表示する。また、必須インジケータは表示しない。
    if (this.selectedCard === PaymentRequestPaymentCard.RegisteredCardTypeEnum.PaymentCard1) {
      this._serviceRequestPaymentInputService.isDisplayCardNumberAsTextBox = false;
      // [6.10.1.1] クレジットカード1が選択されている場合、予約基本情報取得API応答.data.confidential.paymentCard1.cardNumber
      this.paymentInputFG.get('cardNumber')?.setValue(this.amc.confidential?.paymentCard1?.cardNumber);
      if (!isStringEmpty(this.amc.confidential?.paymentCard1?.expiryDate)) {
        expiryDate = this.amc.confidential?.paymentCard1?.expiryDate ?? '';
      }
    } else if (this.selectedCard === PaymentRequestPaymentCard.RegisteredCardTypeEnum.PaymentCard2) {
      this._serviceRequestPaymentInputService.isDisplayCardNumberAsTextBox = false;
      // [6.10.1.2] クレジットカード2が選択されている場合、予約基本情報取得API応答.data.confidential.paymentCard2.cardNumber
      this.paymentInputFG.get('cardNumber')?.setValue(this.amc.confidential?.paymentCard2?.cardNumber);
      if (!isStringEmpty(this.amc.confidential?.paymentCard2?.expiryDate)) {
        expiryDate = this.amc.confidential?.paymentCard2?.expiryDate ?? '';
      }
    } else {
      this._serviceRequestPaymentInputService.isDisplayCardNumberAsTextBox = true;
      // [6.10.2] 上記以外の場合、テキストボックスを空欄で表示し、あわせて必須インジケータを表示する。
      if (!this.paymentInputFG.get('cardNumber')?.value) {
        this.paymentInputFG.get('cardNumber')?.setValue('');
      }
      // [6.11.2] 上記以外の場合、""""(空欄)
      if (!this.paymentInputFG.get('expiryDate')?.value) {
        this.paymentInputFG.get('expiryDate')?.setValue('');
      }
      if (!this.paymentInputFG.get('holderName')?.value) {
        this.paymentInputFG.get('holderName')?.setValue('');
      }
      if (!this.paymentInputFG.get('cvv')?.value) {
        this.paymentInputFG.get('cvv')?.setValue('');
      }
    }

    const currentMonth = expiryDate.substring(0, 2);
    const expiryYear = Number('20' + expiryDate.substring(2, 4));
    if (!isStringEmpty(expiryDate) && expiryYear >= this.yearRange[0] && expiryYear <= this.yearRange[1]) {
      // [6.11.1.1] クレジットカード1が選択されている、かつ予約基本情報取得API応答.data.confidential.paymentCard1.expiryDateのyyyyが有効期限の選択値に存在する場合、予約基本情報取得API応
      // [6.11.1.2] クレジットカード2が選択されている、かつ予約基本情報取得API応答.data.confidential.paymentCard2.expiryDateのyyyyが有効期限の選択値に存在する場合、予約基本情報取得API応
      this.paymentInputFG.get('expiryDate')?.setValue(String(expiryYear) + currentMonth);
    } else {
      // [6.11.1.3] 予約基本情報取得APIより取得した値が有効期限の選択値に存在しない場合、""(空欄)
      if (!this.paymentInputFG.get('expiryDate')?.value) {
        this.paymentInputFG.get('expiryDate')?.setValue('');
      }
    }
  }

  initDisplayCardSelecting(amc: GetBasicReservationInformationResponseData) {
    // [6.1.1.1] ユーザ共通情報.ログインステータス=“REAL_LOGIN。
    return (
      this.isRealLogin &&
      // [6.1.1.2] 予約基本情報API応答.data.generalCreditCardName1≠””(空欄)、または　generalCreditCardName2≠””(空欄)。
      (amc?.generalCreditCardName1 !== '' || this.amc?.generalCreditCardName2 !== '') &&
      // [6.1.1.3] ユーザ共通情報.POS国コード≠”IN”(インド)。
      this._common.aswContextStoreService.aswContextData.posCountryCode !== 'IN'
    );
    // [6.1.2] 選択中の項目はラベル表示とし、あわせて選択中を示すチェックマークを表示する。枠線はネイビーにする。
  }
  initIssuerReceiptEmailInput(pnr: GetOrderResponseData) {
    const issuerReceiptEmail = addIssuerReceiptEmailInput();
    this._serviceRequestPaymentInputService.paymentInputFG.addControl('issuerReceiptEmail', issuerReceiptEmail);
    const issuerReceiptEmailValue =
      stringEmptyDefault(pnr?.travelers?.[0]?.names?.[0].lastName) +
      ' ' +
      stringEmptyDefault(pnr?.travelers?.[0]?.names?.[0]?.firstName);
    this.paymentInputFG.get('issuerReceiptEmail')?.patchValue(issuerReceiptEmailValue);
  }

  onDisplayBrands(availableCardBrandList: string[]) {
    // [6.8.3] 利用可能なカードブランドリストの件数分、繰返し当該ブランドに紐づくブランドロゴラベルを出力する。
    const cardBrandImageList: any = [];
    availableCardBrandList.forEach((brand) => {
      const brandImage: BrandImage = {};
      switch (brand) {
        // VI: VISA
        case 'VI':
          brandImage.src = 'cardlogo_visa.png';
          brandImage.alt = 'VISA Card';
          break;
        // CA: MASTER CARD
        case 'CA':
          brandImage.src = 'cardlogo_mastercard.png';
          brandImage.alt = 'Master Card';
          break;
        // JC: JBC
        case 'JC':
          brandImage.src = 'cardlogo_jcb.png';
          brandImage.alt = 'JCB Card';
          break;
        // AX: AMEX
        case 'AX':
          brandImage.src = 'cardlogo_americanexpress.png';
          brandImage.alt = 'American Express';
          break;
        // DS: DISCOVER
        case 'DS':
          brandImage.src = 'cardlogo_discover.png';
          brandImage.alt = 'DISCOVER Card';
          break;
        // DC: DINERS
        case 'DC':
          brandImage.src = 'cardlogo_dinersclub.png';
          brandImage.alt = 'Diners Club INTERNATIONAL';
          break;
        // TP: UATP
        case 'TP':
          brandImage.src = 'cardlogo_uatp.png';
          brandImage.alt = 'UATP Card';
          break;
      }
      if (brandImage) {
        cardBrandImageList.push({ src: brandImage.src, alt: brandImage.alt });
      }
    });
    return cardBrandImageList;
  }
  getPhoneCountryExtension() {
    // [6.18.1] 画面.電話番号の国番号選択で国名称を選択している場合、"+"(プラス) + 画面.電話番号の国番号選択に選択している国の国コード(2 レター)をキーとし、イベントID(1)で作成した電話番号対象国(マップ)から国番号を取得し、ラベル表示する。
    if (this.paymentInputFG.get('cardHolderPhoneCountry')?.value) {
      this.paymentInputFG.get('cardHolderPhoneNumber')?.enable();
      this._serviceRequestPaymentInputService.cardHolderCountryPhoneExtension = `+${
        this.countryMap.get(this.paymentInputFG.get('cardHolderPhoneCountry')?.value) ?? ''
      }`;
    } else {
      // [6.19.※]  電話番号の国番号選択が未選択の場合、非活性とする。
      this.paymentInputFG.get('cardHolderPhoneNumber')?.disable();
      this._serviceRequestPaymentInputService.cardHolderCountryPhoneExtension = '';
    }
  }

  // (5) 新しいカードボタン押下処理
  onClickNewCard() {
    // enable card number validation
    enableCardNumberValidation(this.paymentInputFG);
    // (5.1) 画面.新しいカードボタンを選択状態とし、画面.クレジットカード1と画面.クレジットカード2を未選択にする。
    this._serviceRequestPaymentInputService.selectedCard = PAYMENT_REQUEST_PAYMENT_CARD_DEFAULT.NEW_CARD;
    // (5.2) 画面.クレジットカード番号と画面.カード有効期限を空欄にし、画面.クレジットカード番号はテキストボックスとする。
    this.paymentInputFG.get('cardNumber')?.setValue('');
    this.paymentInputFG.get('expiryDate')?.setValue('');
    this.paymentInputFG.get('holderName')?.setValue('');
    this.paymentInputFG.get('cvv')?.setValue('');

    this._serviceRequestPaymentInputService.isDisplayCardNumberAsTextBox = true;
    // (5.3) ユーザ共通情報.端末種別=”SP" かつ ユーザ共通情報.ログインステータス= "REAL_LOGIN"(リアルログイン)の場合、
    if (this.isSp && this.isRealLogin) {
      // (5.3.1)  画面.選択中のカードに画面.使用するクレジットカード選択の値に表示されている文言と同じ文言を表示する。
      this._serviceRequestPaymentInputService.isSelectedCardSP = true;
    }
    this.paymentInputFG.markAsUntouched();
    this.paymentInputFG.markAsPristine();
    this._changeDetectorRef.markForCheck();
  }

  // (6) クレジットカード1ボタン押下処理
  onClickPaymentCard1() {
    // (6.1) 画面.クレジットカード1ボタンを選択状態とし、画面.新しいカードと画面.クレジットカード2を未選択にする。
    this._serviceRequestPaymentInputService.selectedCard =
      PaymentRequestPaymentCard.RegisteredCardTypeEnum.PaymentCard1;
    this.paymentInputFG.get('holderName')?.setValue('');
    this.paymentInputFG.get('cvv')?.setValue('');
    // (6.2) パスワード認証実施済み判定=false(未認証)の場合、パスワード入力(R01-M081)をモーダルで表示する。
    if (!this._serviceRequestPaymentInputService.isPasswordAuthenticationCompleted) {
      const part = passwordInputPayloadParts();
      part.closeEvent = (isSuccess?: boolean) => this.paymentCardCloseProcess(isSuccess, this.paymentCard1);
      part.closeBackEnable = true;
      this._modalService.showSubPageModal(part);
    }
    // (6.3) 上記以外の場合、以下のパラメータで、「当画面共通処理No1.パスワード入力の認証ボタン後処理」を実施する。
    else {
      this._serviceRequestPaymentInputService.handleCommonNo1();
    }
    this.paymentInputFG.markAsUntouched();
    this.paymentInputFG.markAsPristine();
    this._changeDetectorRef.markForCheck();
  }

  // (7) クレジットカード2ボタン押下処理
  onClickPaymentCard2() {
    // (7.1) 画面.クレジットカード2ボタンを選択状態とし、画面.新しいカードと画面.クレジットカード1を未選択にする。
    this._serviceRequestPaymentInputService.selectedCard =
      PaymentRequestPaymentCard.RegisteredCardTypeEnum.PaymentCard2;
    this.paymentInputFG.get('holderName')?.setValue('');
    this.paymentInputFG.get('cvv')?.setValue('');
    // (7.2) パスワード認証実施済み判定=false(未認証)の場合、パスワード入力(R01-M081)をモーダルで表示する。
    if (!this._serviceRequestPaymentInputService.isPasswordAuthenticationCompleted) {
      const part = passwordInputPayloadParts();
      part.closeEvent = (isSuccess?: boolean) => this.paymentCardCloseProcess(isSuccess, this.paymentCard2);
      part.closeBackEnable = true;
      this._modalService.showSubPageModal(part);
    }
    // (7.3) 上記以外の場合、以下のパラメータで、「当画面共通処理No1.パスワード入力の認証ボタン後処理」を実施する。
    else {
      this._serviceRequestPaymentInputService.handleCommonNo1();
    }
    this.paymentInputFG.markAsUntouched();
    this.paymentInputFG.markAsPristine();
    this._changeDetectorRef.markForCheck();
  }

  paymentCardCloseProcess(isSuccess?: boolean, paymentCard?: PaymentRequestPaymentCard.RegisteredCardTypeEnum) {
    if (isSuccess === false) {
      this._serviceRequestPaymentInputService.handleCommonNo2();
    } else {
      this._serviceRequestPaymentInputService.handleCommonNo1();
    }
    this._changeDetectorRef.markForCheck();
  }

  /**
   * (8) カード変更ボタン押下処理※SP版
   */
  onChangeSelectedCardSp() {
    // (8.1)	画面.選択済みの使用するクレジットカードを非表示する。
    this._serviceRequestPaymentInputService.isSelectedCardSP = false;
  }

  /**
   * (9) UATPカード選択チェック処理 ※チェックボックス切り替え
   */
  public onChangeUatpCardSelection() {
    // (9.1)	画面.UATPカード選択チェックが選択されている場合、画面.CVVに””(空欄)を設定し、画面.CVVを非活性にする。
    const uATPCardChecked = this.paymentInputFG?.get('uATPCardChecked');
    const cvvControl = this.paymentInputFG?.get('cvv');
    if (uATPCardChecked?.value) {
      cvvControl?.disable();
    }
    // (9.2)	上記以外の場合、画面.CVVを活性にする。
    else {
      cvvControl?.enable();
    }
    cvvControl?.reset();
  }

  /**
   * (10)	電話番号の国番号選択プルダウン押下処理
   */
  onChangeCountry() {
    // (10.1)	画面.電話番号の国番号を「表示内容」シートの「初期表示」の当該項目に従って表示する。
    this.getPhoneCountryExtension();
  }

  // (14.1.1.2.1) 名義人メールアドレスが存在する場合、名義人メールアドレスと名義人メールアドレス(確認用)が一致しない場合、E0461を表示する。
  emailChange() {
    updateConfirmEmailValidator(this.paymentInputFG, 'cardHolderEmail', 'cardHolderConfirmEmail');
  }

  reload(): void {
    /* override abstract reload function of base class */
  }

  destroy(): void {
    if (this._subscriptions) {
      this._subscriptions.unsubscribe();
    }
  }
}
